import { isHolidayOrWeekend } from '@freightview/holidays'
import { useShallow } from 'zustand/shallow'

import type { Quote } from '@fv/client-types'
import { fvDate } from '@fv/dates'
import type { LocationType, Workflow } from '@fv/models'
import type { SlimLoadItem } from '@fv/models/core'

import { useUserSettings } from '@/hooks/settings'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'

import { quoteFuncs } from '../quote/quoteFuncs'
import { useBookingFormCtx } from './BookingFormProvider'

// Form stop dates are local-midnight Dates; isHolidayOrWeekend evaluates in UTC,
// so compare using the local calendar day to avoid shifting in forward timezones.
export function isPickupDateHolidayOrWeekend(
  locations: Array<{ type: LocationType; stopDate?: Date | null }>,
) {
  const stopDate = locations.find(loc => loc.type === 'origin')?.stopDate
  return isHolidayOrWeekend(
    stopDate ? fvDate(stopDate).format('YYYY-MM-DD') : undefined,
  )
}

interface CanSchedulePickupProps {
  isFreightCollect?: boolean
  workflow: Workflow
  locations: Array<{ type: LocationType; stopDate?: Date }>
  items: SlimLoadItem[]
  customerRoutedCarrierId?: string
  selectedQuote?: Quote
}

export function useCanSchedulePickup({
  isFreightCollect,
  workflow,
  locations,
  items,
  customerRoutedCarrierId: carrierId,
  selectedQuote,
}: CanSchedulePickupProps) {
  const settingsQuery = useUserSettings()
  const accountCarriers = settingsQuery.data?.carriers ?? []
  const quoteCarrierSettings = useCarrierPublicSettings(
    selectedQuote?.carrierId,
  ).carrierPublicSettings
  const crCarrierSettings = useCarrierPublicSettings(carrierId)

  const provisionedCarrier = accountCarriers.find(
    c => c._id === carrierId || c._id === selectedQuote?.carrierId,
  )

  if (quoteFuncs.isSpotWorkflow(selectedQuote)) return true

  const carrierDispatchDisabled =
    quoteCarrierSettings?.dispatchMethod === 'disabled'
  if (carrierDispatchDisabled) {
    return false
  }
  const isManualQuote = !selectedQuote || selectedQuote.method === 'manual'
  if (
    !isFreightCollect &&
    isManualQuote &&
    (provisionedCarrier?.status !== 'visible' ||
      !quoteCarrierSettings?.isApiConnected)
  ) {
    return false
  }
  if (
    isFreightCollect &&
    (!crCarrierSettings?.carrierPublicSettings?.isApiConnected ||
      crCarrierSettings?.carrierPublicSettings?.dispatchMethod === 'disabled')
  ) {
    return false
  }

  return !isPickupDateHolidayOrWeekend(locations)
}

export function useBookingFormCanSchedulePickup() {
  const isFreightCollect = useBookingFormCtx(s => s.isFreightCollect)
  const workflow = useBookingFormCtx(s => s.workflow)
  const locations = useBookingFormCtx(s => s.locations)
  const items = useBookingFormCtx(s => s.items)
  const { carrierId, selectedQuote } = useBookingFormCtx(
    useShallow(s => ({
      carrierId: s.customerRoutedDetail.carrier?.id,
      selectedQuote: s.quote,
    })),
  )

  return useCanSchedulePickup({
    isFreightCollect,
    workflow,
    locations,
    items,
    customerRoutedCarrierId: carrierId,
    selectedQuote,
  })
}
